import { useCallback, useEffect, useRef, useState } from "react"
import { GetApp } from "../../App"
import { useSubscription } from "../../App/useSubscription"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { Loading } from "../../Components/Loading"
import { Overlay, OverlayTitle } from "../../Components/Overlay"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { ApiClient, IResponse, ILogbookAppStatusResult, ILogbookSyncStatus, SyncableApi } from "../../Types"
import { Subscription } from "../../Types/Generated/LogbookApi/Subscriptions"
import { GetAppView } from "../AppView"
import { ShipRoutes } from "./Router"
import { GetShipView } from "./Context"
import { logger, NATIVE } from "../../App/Logger"

export const ShipSyncStatusViewData = {
  // Sync statuses whose dialog reads as an ERROR (red header) rather than as a
  // warning (amber). Deliberately a table and not a condition: the header style
  // is a judgement about how bad a state is, and widening it is a one-line,
  // reviewable edit here (the whole enum is pinned in the tests).
  //
  // Only `selectShipFailed` qualifies today (issue #1290): the ship did not open
  // and the user cannot continue. The remaining statuses are either progress
  // states -- `selectShip` renders the ordinary "selecting the ship" spinner in
  // this very component, and turning THAT red would be worse than the bug -- or
  // degraded-sync states the user can close and keep working through.
  alarmStatuses: [ILogbookSyncStatus.selectShipFailed] as ILogbookSyncStatus[],

  // The `OverlayTitle` style for a status. Shared by both dialogs below so the
  // in-ship overlay and the app-level message can never drift apart again.
  titleStyle(status: ILogbookSyncStatus): "alarm" | "warning" {
    return ShipSyncStatusViewData.alarmStatuses.includes(status) ? "alarm" : "warning";
  },

  // Localized message key: an explicit error takes precedence over the raw status.
  messageKey(status: ILogbookSyncStatus, error?: string): string {
    return error && error != "" ? error : status;
  },

  // Which action row ShipSyncStatusMessageView renders for a given status/error.
  messageAction(status: ILogbookSyncStatus, error?: string): "cloudAccount" | "close" | "loading" {
    switch (status) {
      case ILogbookSyncStatus.selectShipFailed:
        return error && error == "limitActiveShipsReached" ? "cloudAccount" : "close";
      case ILogbookSyncStatus.selectShip:
        return "loading";
      default:
        return "close";
    }
  },

  // Statuses that show the spinner instead of a close button in the overlay body.
  //
  // `initialSyncFailed` deliberately is NOT one of them (issue #1247): it used to
  // be, which meant the overlay rendered `<Loading />` for a terminal state and
  // the branch carrying the close button was unreachable — a permanent spinner
  // with no retry and no escape, which is what the customer was left staring at.
  showsLoading(status: ILogbookSyncStatus): boolean {
    return status == ILogbookSyncStatus.initialSyncInProgress
      || status == ILogbookSyncStatus.selectShip
      || status == ILogbookSyncStatus.transition;
  },

  // Statuses the user can act on with a manual retry. Only a failed *initial*
  // sync: a failed periodic sync already retries on its own 10s loop.
  showsRetry(status: ILogbookSyncStatus): boolean {
    return status == ILogbookSyncStatus.initialSyncFailed;
  },

  // Ask the app to restart a failed initial sync. This only wakes its background
  // sync loop — it does not wait for the cloud — so the request returns straight
  // away even when the cloud is slow. Repeated calls are safe: the app refuses to
  // put a second initial sync in flight while one is running.
  retrySync(api: ApiClient): Promise<IResponse> {
    return new SyncableApi(api).sync.retry({ params: {} }).call();
  },

  // Statuses that mean the sync is settled, so the overlay auto-hides.
  shouldAutoClose(status?: ILogbookSyncStatus): boolean {
    return status == ILogbookSyncStatus.current
      || status == ILogbookSyncStatus.dataWaiting
      || status == ILogbookSyncStatus.paused;
  },

};

interface ShipSyncStatusViewProps {
}

export function ShipSyncStatusView(_: ShipSyncStatusViewProps) {
  const app = GetApp();
  const t = app.getT("ShipSyncStatusView");
  const [status, setStatus] = useState<ILogbookSyncStatus | undefined>(app.status?.sync?.status);
  const { navigateShip, shipOverlay: _shipOverlay, resetShipOverlay } = GetShipView();
  const initialSyncCompleted = app.current?.initialSyncCompleted === true;

  const hide = useCallback(() => {
    resetShipOverlay();
    navigateShip(ShipRoutes.calendar, undefined, true);
  }, []);

  // Bumped by every status change that is NEWER than a poll already in flight (a retry
  // press, a broadcast). The refresh below reads it before and after awaiting, so a
  // slow read can never overwrite something the user just did -- which would undo the
  // optimistic spinner the retry action deliberately shows.
  const statusGeneration = useRef(0);
  const applyStatus = useCallback((next: ILogbookSyncStatus) => {
    statusGeneration.current += 1;
    setStatus(next);
  }, []);

  // @mutle's decision: the retry action only *requests* a retry — it wakes the
  // app's background sync loop rather than driving a cloud call the UI has to
  // wait on — but the overlay must reflect it at once instead of sitting on the
  // error until the next status broadcast arrives. If the request itself fails
  // we fall back to the error state, so a rejection can't strand the overlay on
  // the spinner it optimistically switched to.
  const retry = useCallback(async () => {
    applyStatus(ILogbookSyncStatus.initialSyncInProgress);
    try {
      await ShipSyncStatusViewData.retrySync(app.apiClient);
    } catch (error) {
      logger.debug("sync retry request failed", error, NATIVE);
      applyStatus(ILogbookSyncStatus.initialSyncFailed);
    }
  }, [app.apiClient, applyStatus]);

  // Re-read the authoritative sync status rather than waiting for it to be pushed.
  //
  // This overlay is a blocking modal whose only exit was a live `AppStatus` broadcast --
  // a single event with two ways to be missed. Selecting a ship resets the app instance,
  // so the client's socket is commonly reconnecting exactly when the status flips; and
  // the flip can land between the snapshot `useState` takes at mount and the
  // subscription registering in the effect after it. Either way the app had finished
  // syncing and the user sat on the spinner indefinitely, with nothing to retry --
  // observed with the overlay still up two minutes after the app logged "Initial sync
  // complete", and not one `sync status` line in the browser console.
  //
  // Polling only while a loading state is displayed: re-reading a real status is a
  // readiness check, and it stops the moment the overlay has something to show the user.
  const refreshStatus = useCallback(async () => {
    const generation = statusGeneration.current;
    try {
      const latest = await app.updateStatus();
      if (statusGeneration.current !== generation) {
        return;
      }
      if (latest?.sync?.status) {
        applyStatus(latest.sync.status);
      }
    } catch (error) {
      // A failed refresh must never replace a real status with an error state: the
      // broadcast path is still live, and the next tick tries again.
      logger.debug("sync status refresh failed", error, NATIVE);
    }
  }, [app, applyStatus]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (status == undefined || !ShipSyncStatusViewData.showsLoading(status)) {
      return;
    }
    const timer = setInterval(() => { void refreshStatus(); }, 10_000);
    return () => clearInterval(timer);
  }, [status, refreshStatus]);

  useEffect(() => {
    if (status == undefined && app.status?.sync?.status) {
      setStatus(app.status.sync.status);
    }
    if (ShipSyncStatusViewData.shouldAutoClose(status)) {
      if (initialSyncCompleted) {
        hide();
        return undefined;
      }
      // Sync status can reach `current` before the selected ship's current-data
      // endpoint is readable. Keep the blocking dialog mounted and refresh that
      // data in place; leaving early mounts the calendar against an incomplete
      // app and replaces the useful progress dialog with a generic Loading page.
      let cancelled = false;
      let refreshing = false;
      const finish = async () => {
        if (refreshing) {
          return;
        }
        refreshing = true;
        try {
          await app.updateCurrent();
        } catch (error) {
          logger.debug("current refresh failed", error, NATIVE);
        }
        refreshing = false;
        if (!cancelled && app.current?.initialSyncCompleted == true) {
          hide();
        }
      };
      void finish();
      const timer = setInterval(() => { void finish(); }, 1_000);
      return () => {
        cancelled = true;
        clearInterval(timer);
      };
    }
    return undefined;
  }, [status, app.status, initialSyncCompleted, hide]);

  useSubscription((newStatus: ILogbookAppStatusResult, _type: string) => {
    logger.debug("sync status", [status, newStatus.sync], NATIVE);
    applyStatus(newStatus.sync?.status || ILogbookSyncStatus.uninitialized);
  }, Subscription.AppStatus);

  if (status == undefined) {
    return;
  }
  return (
    <Overlay className="ShipSyncStatusView">
      <OverlayTitle style={ShipSyncStatusViewData.titleStyle(status)} title={t("title")} />
      <div className="Overlay-body">
        <Block>
          <BlockRow>
            <LocalizedMessage message={(m) => m.noteText[status]} className="message-text-bigger" />
          </BlockRow>

          {ShipSyncStatusViewData.showsLoading(status) ? (
            <BlockRow>
              <Loading />
            </BlockRow>
          ) : (
            <BlockRow actions={true}>
              {/*
                Retry takes the leading field -- the one that is otherwise an empty
                spacer -- rather than being inserted before close. `.block-field` is
                `flex-grow: 1` and only `:last-child` is right-aligned, so this puts
                retry at the overlay's left edge and close at the right, on one line.
                As the middle of three fields it was left-aligned within its own
                third, which read as floating mid-overlay. Not `default`-styled
                either: close is the primary way out, and two accent buttons compete.
              */}
              <BlockField>
                {ShipSyncStatusViewData.showsRetry(status) ? (
                  <button className="button sync-retry-button" onClick={() => retry()}>{t("retry")}</button>
                ) : null}
              </BlockField>
              <BlockField><button className="button default" onClick={() => hide()}>{t("close")}</button></BlockField>
            </BlockRow>
          )}
        </Block>
      </div>
    </Overlay>
  );
}

interface ShipSyncStatusMessageViewProps {
  error?: string;
  status: ILogbookSyncStatus;
}

export function ShipSyncStatusMessageView({ error, status }: ShipSyncStatusMessageViewProps) {
  const app = GetApp();
  const t = app.getT("ShipSyncStatusView");
  const { resetAppOverlay } = GetAppView();

  const hide = useCallback(() => {
    resetAppOverlay();
  }, []);

  let content = null;
  switch (ShipSyncStatusViewData.messageAction(status, error)) {
    case "cloudAccount":
      content = (
        <BlockRow actions={true}>
          <BlockField></BlockField>
          <BlockField>
            <button className="button default" onClick={async () => {
              if (await app.openServer("account")) {
                resetAppOverlay();
              }
            }}
            >{app.t("AppOverlayContentView_ship_view_ships_cloud")}</button>
          </BlockField>
        </BlockRow>
      );
      break;
    case "loading":
      content = (
        <BlockRow>
          <Loading />
        </BlockRow>
      );
      break;
    default:
      content = (
        <BlockRow actions={true}>
          <BlockField></BlockField>
          <BlockField><button className="button default" onClick={() => hide()}>{t("close")}</button></BlockField>
        </BlockRow>
      );
      break;
  }

  return (
    <div className="ShipSyncStatusView">
      <OverlayTitle style={ShipSyncStatusViewData.titleStyle(status)} title={t("title")} />
      <div className="Overlay-body">
        <Block>
          <BlockRow>
            <LocalizedMessage message={(m) => m.noteText[ShipSyncStatusViewData.messageKey(status, error)]} className="message-text-bigger" />
          </BlockRow>

          {content}
        </Block>
      </div>
    </div>
  );
}
